import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { getSessionHeaders } from '../../lib/serverSession';

/**
 * Blocking acknowledgement for signed-in shareholders.
 * Acceptance is stored on terms_acceptance for that user.
 * Logged-out pages, including the public site after logout, do not show it.
 */
const NOTICE_URL = '/api/user/dividend-projection-acknowledgement';

type Gate = 'checking' | 'show' | 'hide';

const listeners = new Set<() => void>();
let dialogOwner: string | null = null;
let gate: Gate = 'checking';
let acceptedSessionId: string | null = null;
let loadPromise: Promise<void> | null = null;

function notify(): void {
  listeners.forEach((listener) => listener());
}

function sessionIdFromHeaders(headers: Record<string, string>): string {
  return headers['X-Session-Id'] || '';
}

async function refreshGate(): Promise<void> {
  const headers = getSessionHeaders();
  const sessionId = sessionIdFromHeaders(headers);
  if (!sessionId) {
    gate = 'hide';
    notify();
    return;
  }
  if (acceptedSessionId === sessionId) {
    gate = 'hide';
    notify();
    return;
  }

  try {
    const response = await fetch(NOTICE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...headers },
      body: JSON.stringify({ operation: 'status' }),
    });
    if (response.status === 401 || response.status === 403) {
      gate = 'hide';
      notify();
      return;
    }
    const data = await response.json();
    if (data?.impersonated) {
      gate = 'hide';
    } else if (data?.accepted) {
      acceptedSessionId = sessionId;
      gate = 'hide';
    } else {
      gate = 'show';
    }
  } catch {
    gate = 'show';
  }
  notify();
}

function ensureGate(): Promise<void> {
  if (!loadPromise) {
    loadPromise = refreshGate().finally(() => {
      loadPromise = null;
    });
  }
  return loadPromise;
}

async function saveAcceptance(): Promise<{ ok: boolean; error?: string }> {
  const headers = getSessionHeaders();
  const sessionId = sessionIdFromHeaders(headers);
  if (!sessionId) return { ok: false, error: 'Sign in again, then accept the notice.' };

  const response = await fetch(NOTICE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ operation: 'accept' }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data?.success) {
    return { ok: false, error: 'We could not save your acknowledgement. Please try again.' };
  }
  acceptedSessionId = sessionId;
  gate = 'hide';
  notify();
  return { ok: true };
}

export const DividendCalculatorNotice: React.FC = () => {
  const instanceId = useId();
  const titleId = useId();
  const [visible, setVisible] = useState(false);
  const [checked, setChecked] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const sync = () => {
      if (cancelled) return;
      if (gate !== 'show') {
        if (dialogOwner === instanceId) dialogOwner = null;
        setVisible(false);
        return;
      }
      if (dialogOwner === null || dialogOwner === instanceId) {
        dialogOwner = instanceId;
        setVisible(true);
      }
    };

    listeners.add(sync);
    ensureGate().then(sync);

    return () => {
      cancelled = true;
      listeners.delete(sync);
      if (dialogOwner === instanceId) {
        dialogOwner = null;
        notify();
      }
    };
  }, [instanceId]);

  useEffect(() => {
    if (!visible) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const blockDismiss = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', blockDismiss, true);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', blockDismiss, true);
    };
  }, [visible]);

  const onAccept = async () => {
    if (!checked || saving) return;
    setSaving(true);
    setSaveError('');
    try {
      const result = await saveAcceptance();
      if (!result.ok) setSaveError(result.error || 'We could not save your acknowledgement. Please try again.');
    } catch {
      setSaveError('We could not save your acknowledgement. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!visible || typeof document === 'undefined') return null;

  const canContinue = checked && !saving;

  return createPortal(
    <div
      role="presentation"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(0, 0, 0, 0.78)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        style={{
          width: 'min(640px, 100%)',
          maxHeight: 'min(88vh, 820px)',
          display: 'flex',
          flexDirection: 'column',
          background: '#111827',
          color: '#f9fafb',
          border: '1px solid rgba(251, 191, 36, 0.45)',
          borderRadius: '16px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.45)',
        }}
      >
        <div style={{ overflow: 'auto', minHeight: 0, flex: '1 1 auto', padding: '24px 24px 12px' }}>
          <h2 id={titleId} style={{ margin: '0 0 12px', fontSize: '1.25rem', lineHeight: 1.3, color: '#fbbf24' }}>
            These figures are projections
          </h2>
          <p style={{ margin: '0 0 12px', fontSize: '0.95rem', lineHeight: 1.6 }}>
            The amounts in this calculator are <strong>projections, not actual dividend payouts</strong>. They are <strong>not guaranteed</strong>.
          </p>
          <p style={{ margin: '0 0 12px', fontSize: '0.95rem', lineHeight: 1.6, color: '#e5e7eb' }}>
            Projected amounts of gold mined are estimates. Once mining and processing begin, the Mine Progress page will show the amount of material processed. Updates will be published weekly, every two weeks, or monthly, depending on results and the time required.
          </p>
          <p style={{ margin: '0 0 8px', fontSize: '0.95rem', fontWeight: 700 }}>
            How a dividend is calculated
          </p>
          <ol style={{ margin: '0 0 12px', paddingLeft: '1.4rem', fontSize: '0.95rem', lineHeight: 1.6, color: '#e5e7eb', listStyle: 'decimal' }}>
            <li>Gold and other minerals are mined.</li>
            <li>The minerals are sold to the refinery at the market price, less 5%.</li>
            <li>Company expenses of 50% are deducted.</li>
            <li>
              The remaining balance is divided by the applicable number of shares. For the January 2027 and April 2027 dividend periods, profit is divided by the number of shares sold. After those periods, profit is divided by 1,400,000 shares.
            </li>
            <li>That result is the dividend per share.</li>
            <li>
              The dividend per share is multiplied by the eligible shares you own. Only shares purchased at least three months before the dividend date qualify.
            </li>
          </ol>
          <p style={{ margin: '0 0 10px', fontSize: '0.95rem', lineHeight: 1.6, color: '#e5e7eb' }}>
            Dividends are based on minerals that are mined and sold. They are not based on money received from share purchases.
          </p>
          <p style={{ margin: '0 0 16px', fontSize: '0.95rem', lineHeight: 1.6, color: '#e5e7eb' }}>
            Money from share purchases is used for mine setup, company running costs, referral commissions, the daily performance pool, and the weekly Gold Diggers pool.
          </p>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#9ca3af' }}>
            Aureus Alliance Holdings
          </p>
        </div>
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.12)', padding: '14px 24px 18px', background: '#111827', borderRadius: '0 0 16px 16px' }}>
          <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '12px', fontSize: '0.95rem', lineHeight: 1.5, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={checked}
              onChange={(event) => setChecked(event.target.checked)}
              style={{ marginTop: '4px', width: '18px', height: '18px', flexShrink: 0 }}
            />
            <span>I understand that these figures are projections and are not guaranteed dividend payouts.</span>
          </label>
          {saveError ? (
            <p style={{ margin: '0 0 10px', color: '#fecaca', fontSize: '0.9rem' }}>{saveError}</p>
          ) : null}
          <button
            type="button"
            onClick={onAccept}
            disabled={!canContinue}
            style={{
              width: '100%',
              border: 'none',
              borderRadius: '10px',
              padding: '12px 16px',
              fontSize: '0.95rem',
              fontWeight: 700,
              cursor: canContinue ? 'pointer' : 'not-allowed',
              background: canContinue ? '#fbbf24' : '#4b5563',
              color: canContinue ? '#111827' : '#d1d5db',
            }}
          >
            {saving ? 'Saving...' : 'Continue'}
          </button>
        </div>
      </section>
    </div>,
    document.body,
  );
};
